import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ROLE_LABELS } from "@/auth";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const SELECTABLE_ROLES = [
  "admin_candidato",
  "coord_estatal",
  "coord_municipal",
  "coord_regional",
  "coord_colonia",
];

export default function SubaccountNew() {
  const nav = useNavigate();
  const [states, setStates] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [form, setForm] = useState({
    name: "",
    candidate_name: "",
    scope_type: "estatal",
    entidad_clave: "",
    municipio_clave: "",
    distrito_clave: "",
    enabled_roles: ["admin_candidato", "coord_estatal", "coord_municipal", "coord_regional", "coord_colonia"],
    admin_email: "",
    admin_password: "",
    admin_name: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/catalog/states").then((r) => setStates(r.data));
  }, []);

  useEffect(() => {
    if (form.entidad_clave) {
      api
        .get("/catalog/municipios", { params: { entidad_clave: form.entidad_clave } })
        .then((r) => setMunicipios(r.data));
    } else {
      setMunicipios([]);
    }
  }, [form.entidad_clave]);

  const toggleRole = (r) => {
    setForm((f) => ({
      ...f,
      enabled_roles: f.enabled_roles.includes(r)
        ? f.enabled_roles.filter((x) => x !== r)
        : [...f.enabled_roles, r],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.scope_type === "municipal" && !form.municipio_clave) {
      toast.error("Selecciona un municipio");
      return;
    }
    if ((form.scope_type === "distrital_federal" || form.scope_type === "distrital_local") && !form.distrito_clave) {
      toast.error("Ingresa el número de distrito");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        entidad_clave: parseInt(form.entidad_clave, 10),
        municipio_clave: form.municipio_clave ? parseInt(form.municipio_clave, 10) : null,
        distrito_clave: form.distrito_clave ? parseInt(form.distrito_clave, 10) : null,
      };
      const r = await api.post("/superadmin/subaccounts", payload);
      toast.success("Subcuenta creada. El catálogo geoelectoral se está cargando…");
      nav(`/super-admin/subaccounts/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error creando subcuenta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subaccount-new-page">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-6 py-10">
        <button onClick={() => nav("/super-admin")} className="text-sm tracking-label text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="back-superadmin">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <h1 className="mt-4 font-display font-black text-3xl sm:text-4xl text-slate-900">
          Nueva subcuenta
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Cada subcuenta representa una campaña independiente con su propio
          enlace, admin responsable y alcance geográfico.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-8" data-testid="subaccount-form">
          {/* Basics */}
          <Section title="Información de la campaña">
            <Field label="Nombre interno" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Ej. Campaña Juan Pérez 2027" data-testid="input-sub-name" />
            </Field>
            <Field label="Nombre del candidato/a" required>
              <Input value={form.candidate_name} onChange={(e) => setForm({ ...form, candidate_name: e.target.value })} required placeholder="Nombre completo" data-testid="input-candidate-name" />
            </Field>
          </Section>

          {/* Scope */}
          <Section title="Alcance geográfico">
            <Field label="Tipo de alcance" required>
              <RadioGroup
                value={form.scope_type}
                onValueChange={(v) => setForm({ ...form, scope_type: v, municipio_clave: "", distrito_clave: "" })}
                className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2"
              >
                <label className="flex items-center gap-2 cursor-pointer surface-card p-3 hover:border-slate-400" data-testid="scope-estatal">
                  <RadioGroupItem value="estatal" />
                  <span className="text-sm">ESTATAL</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer surface-card p-3 hover:border-slate-400" data-testid="scope-distrital-federal">
                  <RadioGroupItem value="distrital_federal" />
                  <span className="text-sm">DISTRITAL FEDERAL</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer surface-card p-3 hover:border-slate-400" data-testid="scope-distrital-local">
                  <RadioGroupItem value="distrital_local" />
                  <span className="text-sm">DISTRITAL LOCAL</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer surface-card p-3 hover:border-slate-400" data-testid="scope-municipal">
                  <RadioGroupItem value="municipal" />
                  <span className="text-sm">MUNICIPAL</span>
                </label>
              </RadioGroup>
            </Field>
            <Field label="Estado / Entidad federativa" required>
              <Select
                value={String(form.entidad_clave || "")}
                onValueChange={(v) => setForm({ ...form, entidad_clave: v, municipio_clave: "" })}
              >
                <SelectTrigger data-testid="select-state-trigger"><SelectValue placeholder="Selecciona un estado" /></SelectTrigger>
                <SelectContent>
                  {states.map((s) => (
                    <SelectItem key={s.clave} value={String(s.clave)} data-testid={`state-option-${s.clave}`}>
                      {s.clave.toString().padStart(2, "0")} · {s.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {form.scope_type === "municipal" && (
              <Field label="Municipio" required>
                <Select
                  value={String(form.municipio_clave || "")}
                  onValueChange={(v) => setForm({ ...form, municipio_clave: v })}
                  disabled={!form.entidad_clave}
                >
                  <SelectTrigger data-testid="select-municipio-trigger"><SelectValue placeholder={form.entidad_clave ? "Selecciona un municipio" : "Primero selecciona el estado"} /></SelectTrigger>
                  <SelectContent>
                    {municipios.map((m) => (
                      <SelectItem key={m.municipio_clave} value={String(m.municipio_clave)} data-testid={`muni-option-${m.municipio_clave}`}>
                        {m.municipio_clave.toString().padStart(3, "0")} · {m.municipio_nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {(form.scope_type === "distrital_federal" || form.scope_type === "distrital_local") && (
              <Field label={form.scope_type === "distrital_federal" ? "Número de distrito federal" : "Número de distrito local"} required>
                <Input
                  type="number"
                  min={1}
                  value={form.distrito_clave}
                  onChange={(e) => setForm({ ...form, distrito_clave: e.target.value })}
                  placeholder="Ej. 5"
                  data-testid="input-distrito-clave"
                />
              </Field>
            )}
          </Section>

          {/* Roles */}
          <Section title="Perfiles habilitados">
            <p className="text-sm text-slate-600 -mt-2 mb-4">
              Selecciona los roles que tendrán acceso a esta subcuenta. El rol{" "}
              <strong>Admin Candidato</strong> siempre se incluye.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SELECTABLE_ROLES.filter((r) => !(form.scope_type === "municipal" && r === "coord_estatal")).map((r) => (
                <label key={r} className="flex items-center gap-3 surface-card p-4 hover:border-slate-400 transition-colors cursor-pointer" data-testid={`role-toggle-${r}`}>
                  <Checkbox
                    checked={form.enabled_roles.includes(r)}
                    onCheckedChange={() => toggleRole(r)}
                    disabled={r === "admin_candidato"}
                  />
                  <div>
                    <div className="font-medium text-slate-900">{ROLE_LABELS[r]}</div>
                    <div className="text-xs text-slate-500">
                      {r === "admin_candidato" && "Acceso total a la subcuenta"}
                      {r === "coord_estatal" && "Visibilidad y enlaces a nivel estatal"}
                      {r === "coord_municipal" && "Visibilidad y enlaces a nivel municipal"}
                      {r === "coord_regional" && "Solo ve registros de su zona/región"}
                      {r === "coord_colonia" && "Solo ve registros de su colonia"}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </Section>

          {/* Admin */}
          <Section title="Admin responsable">
            <p className="text-sm text-slate-600 -mt-2 mb-2">
              Este será el primer usuario de la subcuenta con rol Admin Candidato.
            </p>
            <Field label="Nombre completo" required>
              <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} required data-testid="input-admin-name" />
            </Field>
            <Field label="Correo electrónico" required>
              <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} required data-testid="input-admin-email" />
            </Field>
            <Field label="Contraseña inicial" required>
              <Input type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} required minLength={6} data-testid="input-admin-password" />
            </Field>
          </Section>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={() => nav("/super-admin")} data-testid="cancel-button">Cancelar</Button>
            <Button type="submit" disabled={submitting} className="btn-primary" data-testid="submit-subaccount-button">
              <Save className="w-4 h-4 mr-2" /> {submitting ? "Creando…" : "Crear subcuenta"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="surface-card p-6">
      <h2 className="font-display font-bold text-lg text-slate-900 mb-4 pb-3 border-b border-slate-200">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
